import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import type { StudentDTO } from "../../domain/contracts";
import { studentsApi } from "../api/client";
import {
  useBulkDeleteStudents,
  useBulkToggleStudentStatus,
  useDeleteStudent,
  useStudentsQuery,
} from "../api/queries";
import { exportStudentsCsv } from "../lib/students-export";
import { StudentsFilter } from "../ui/StudentsFilter";
import { StudentsTable } from "../ui/StudentsTable";
import { StudentsToolbar } from "../ui/StudentsToolbar";

const EXPORT_PAGE_SIZE = 200;

async function fetchAllStudentsForExport(
  query: OffsetPageQueryDTO,
): Promise<StudentDTO[]> {
  const all: StudentDTO[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;

  while (offset < total) {
    const page = await studentsApi.list({
      ...query,
      offset,
      limit: EXPORT_PAGE_SIZE,
    });
    all.push(...page.data);
    total = page.meta.total;
    offset += EXPORT_PAGE_SIZE;
    if (page.data.length === 0) break;
  }

  return all;
}

export function StudentsPage() {
  const nav = useNavigate({ from: "/app/students" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/students",
  });
  const [exporting, setExporting] = useState(false);

  const list = useStudentsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["students:create", "packages:sell"]);
  const deleteStudent = useDeleteStudent();
  const bulkDelete = useBulkDeleteStudents();
  const bulkToggleStatus = useBulkToggleStudentStatus();

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const students = await fetchAllStudentsForExport({
        offset: 0,
        limit: EXPORT_PAGE_SIZE,
        sort: search.sort,
        filters: search.filters,
      });
      if (students.length === 0) {
        toast.error("ບໍ່ມີຂໍ້ມູນຕາມເງື່ອນໄຂທີ່ເລືອກ");
        return;
      }
      exportStudentsCsv(students);
      toast.success(`ສົ່ງອອກ CSV ${students.length} ລາຍການ`);
    } catch {
      toast.error("ສົ່ງອອກ CSV ລົ້ມເຫຼວ");
    } finally {
      setExporting(false);
    }
  };

  const handleBulkDelete = async (ids: string[]) => {
    await toast.promise(bulkDelete.run(ids), {
      loading: "ກຳລັງລຶບ...",
      success: (result) => `ລຶບນັກຮຽນ ${result.deletedCount} ຄົນສຳເລັດ`,
      error: "ລຶບນັກຮຽນລົ້ມເຫຼວ",
    });
  };

  const handleBulkToggleStatus = async (
    ids: string[],
    status: "active" | "deactivated",
  ) => {
    const statusLabel = status === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";
    await toast.promise(bulkToggleStatus.run({ ids, status }), {
      loading: "ກຳລັງປ່ຽນສະຖານະ...",
      success: (result) => `${statusLabel}ນັກຮຽນ ${result.updatedCount} ຄົນສຳເລັດ`,
      error: "ປ່ຽນສະຖານະນັກຮຽນລົ້ມເຫຼວ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ນັກຮຽນ" },
          ]}
        />
      </Header>

      <Main>
        <StudentsToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/students/create" })}
          onExportCsv={handleExportCsv}
          exporting={exporting}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <StudentsFilter />

          <StudentsTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onViewProfile={(student) =>
              nav({
                to: "/app/students/$id",
                params: { id: student.id },
              })
            }
            onEdit={(student) =>
              nav({
                to: "/app/students/$id/edit",
                params: { id: student.id },
              })
            }
            onToggleStatus={async (student) => {
              const newStatus =
                student.status === "active" ? "deactivated" : "active";
              try {
                await studentsApi.update(student.id, { status: newStatus });
                toast.success("ປ່ຽນສະຖານະນັກຮຽນສຳເລັດ");
                list.refetch();
              } catch {
                toast.error("ປ່ຽນສະຖານະນັກຮຽນລົ້ມເຫຼວ");
              }
            }}
            onDelete={async (id) =>
              toast.promise(deleteStudent.run(id), {
                loading: "ກຳລັງລຶບ...",
                success: "ລຶບນັກຮຽນສຳເລັດ",
                error: "ລຶບນັກຮຽນລົ້ມເຫຼວ",
              })
            }
            onBulkDelete={handleBulkDelete}
            onBulkToggleStatus={handleBulkToggleStatus}
          />
        </div>
      </Main>
    </>
  );
}
